import { Loader2, RefreshCw, Server, BellRing } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useRunUsageSync, useRunReminders } from "@/hooks/useAdmin";
import { ApiError } from "@/lib/api";

/**
 * Manual trigger for the 20i usage sync pass — useful right after
 * provisioning a new package when the operator doesn't want to wait
 * for the 5-minute timer.
 */
export function AdminSyncPage() {
  const sync = useRunUsageSync();
  const reminders = useRunReminders();
  const { toast } = useToast();

  async function run() {
    try {
      const r = await sync.mutateAsync();
      toast({
        title: "Sync complete",
        description: `${r.containers_updated} updated of ${r.containers_checked} checked · ${r.elapsed_ms}ms`,
        variant: "success"
      });
    } catch (err) {
      toast({
        title: "Sync failed",
        description: err instanceof ApiError ? err.message : "Network error.",
        variant: "destructive"
      });
    }
  }

  async function runReminders() {
    try {
      const r = await reminders.mutateAsync();
      toast({
        title: "Reminder scan complete",
        description: `${r.reminders_sent} sent · ${r.plans_checked} plans, ${r.domains_checked} domains checked · ${r.elapsed_ms}ms`,
        variant: "success"
      });
    } catch (err) {
      toast({
        title: "Reminder scan failed",
        description: err instanceof ApiError ? err.message : "Network error.",
        variant: "destructive"
      });
    }
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">Sync</h2>
        <p className="text-sm text-muted-foreground">
          The 20i usage poller runs every 5 minutes. Trigger one now for impatient operators.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>20i usage pass</CardTitle>
          <CardDescription>
            Walks every active container with a TwentyIPackageId and refreshes its mailbox + storage counts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={run} disabled={sync.isPending}>
            {sync.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Run now
          </Button>

          {sync.data && (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Checked" value={sync.data.containers_checked} />
              <Stat label="Updated" value={sync.data.containers_updated} />
              <Stat label="Failed"  value={sync.data.containers_failed} accent={sync.data.containers_failed > 0 ? "destructive" : "default"} />
              <Stat label="Elapsed" value={`${sync.data.elapsed_ms} ms`} />
            </div>
          )}

          {sync.data && sync.data.failure_notes.length > 0 && (
            <div className="mt-4 space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-destructive">Failures</p>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {sync.data.failure_notes.map((n, i) => (
                  <li key={i} className="rounded border border-destructive/20 bg-destructive/5 px-2 py-1">
                    <Server className="mr-1.5 inline h-3 w-3" />
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Renewal reminders</CardTitle>
          <CardDescription>
            Scans subscriptions + domains and emails customers whose renewal is approaching
            (monthly: 7 + 3 days; quarterly/annual + domains: 15 + 7 days). Idempotent — already-sent
            reminders are skipped.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={runReminders} disabled={reminders.isPending}>
            {reminders.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
            Send reminders now
          </Button>

          {reminders.data && (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Plans"   value={reminders.data.plans_checked} />
              <Stat label="Domains" value={reminders.data.domains_checked} />
              <Stat label="Sent"    value={reminders.data.reminders_sent} />
              <Stat label="Elapsed" value={`${reminders.data.elapsed_ms} ms`} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, accent = "default" }: { label: string; value: number | string; accent?: "default" | "destructive" }) {
  return (
    <div className={accent === "destructive" ? "rounded-md border border-destructive/20 bg-destructive/5 p-3" : "rounded-md border border-border bg-muted/30 p-3"}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-0.5 font-display text-xl font-bold ${accent === "destructive" ? "text-destructive" : ""}`}>{value}</p>
    </div>
  );
}
